import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { circuitSteps, type RoleOf } from './circuit';

/** Seconds a hold is held before autoplay moves on. */
export const MIN_DWELL = 2;
export const MAX_DWELL = 30;
export const DEFAULT_DWELL = 5;

export interface CircuitRunOptions<T> {
  /** The circuit, in the order it is climbed. */
  holds: T[];
  /** How this wall reads a hold's role back — see circuit.ts. */
  roleOf: RoleOf<T>;
  /** The run has moved onto this hold, by its place in `holds`. */
  onEnter?: (index: number) => void;
  /** The clock has stopped, but the run is still where it was. */
  onPause?: () => void;
  /**
   * The run is over, however it ended. `silent` is for the one case where
   * whatever the run was driving is not ours to hand back any more.
   */
  onStop?: (options: { silent: boolean }) => void;
  /** The run reached the top — time to ask for a send. */
  onFinish?: () => void;
}

export type CircuitRun = ReturnType<typeof useCircuitRun>;

/**
 * Walking a circuit hold by hold: where the run is, what moves it, and the clock
 * that moves it on its own.
 *
 * The run walks the circuit's **steps**, which is every hold but the feet: a foot
 * is stood on for the whole climb rather than moved to, so it is shown throughout
 * and the run passes over it (see circuit.ts). A hold the problem comes back to is
 * a step each time it is reached, and a match is one step the climber makes two
 * moves on.
 *
 * Nothing here knows what the run is being watched on. The board lights its wall
 * and claims it while a climber is on it; a photo dims down to the hold in hand
 * and flashes it. Both are `onEnter` and the two ends of it.
 */
export function useCircuitRun<T>({
  holds,
  roleOf,
  onEnter,
  onPause,
  onStop,
  onFinish,
}: CircuitRunOptions<T>) {
  const [running, setRunning] = useState(false);
  const [paused, setPaused] = useState(false);
  // Where the run is, counted in steps rather than in holds: the two only agree
  // on a circuit with no feet in it.
  const [step, setStep] = useState<number | null>(null);
  const [autoplay, setAutoplay] = useState(true);
  const [dwell, setDwell] = useState(DEFAULT_DWELL);

  // The places the run stops at, as indexes into the problem's holds — which is
  // what everything downstream points at, so the two never have to be reconciled.
  const steps = useMemo(() => circuitSteps(holds, roleOf), [holds, roleOf]);
  const last = steps.length - 1;
  const current = step === null ? null : (steps[step] ?? null);

  // The callbacks are read as the run moves rather than as it was set up: a page
  // rebuilds them on every render, and a run that restarted its clock for that
  // would never reach the second hold.
  const handlers = useRef({ onEnter, onPause, onStop, onFinish });
  handlers.current = { onEnter, onPause, onStop, onFinish };

  const goTo = useCallback(
    (at: number) => {
      const index = steps[at];
      if (index === undefined) return;

      setStep(at);
      handlers.current.onEnter?.(index);
    },
    [steps]
  );

  /** Everything that ends a run, whatever ended it. */
  const stop = useCallback(({ silent = false }: { silent?: boolean } = {}) => {
    setRunning(false);
    setPaused(false);
    setStep(null);
    handlers.current.onStop?.({ silent });
  }, []);

  const finish = useCallback(() => {
    stop();
    handlers.current.onFinish?.();
  }, [stop]);

  /**
   * Landing on the last hold is the end of the circuit: in autoplay it is worth
   * the dwell it was given, otherwise there is nothing left to wait for.
   *
   * A paused run stays paused: prev and next move the climber's own way through
   * the circuit — whatever is watching follows them — but nothing starts moving
   * again until they press play.
   */
  const stepTo = useCallback(
    (at: number) => {
      goTo(at);
      if (at >= last && !autoplay) finish();
    },
    [autoplay, finish, goTo, last]
  );

  /**
   * Where a run begins: the first hold that is not a start, since the start holds
   * are the ones the climber is already on. A circuit made only of them (nobody
   * sets that, but the array is the setter's) begins at its first step.
   */
  const begin = useCallback(() => {
    setRunning(true);
    setPaused(false);

    const first = steps.findIndex((index) => roleOf(holds[index]) !== 'start');
    stepTo(first === -1 ? 0 : first);
  }, [holds, roleOf, stepTo, steps]);

  /** Stops the clock; the run stays where it is and can still be stepped by hand. */
  const pause = useCallback(() => {
    setPaused(true);
    handlers.current.onPause?.();
  }, []);

  const resume = useCallback(() => {
    setPaused(false);
    if (step !== null) goTo(step);
  }, [step, goTo]);

  // Autoplay walks the circuit on its own. The timer is rebuilt on every hold, so
  // a manual next or prev gives the new hold a full dwell rather than whatever was
  // left of the last one.
  useEffect(() => {
    if (!running || paused || !autoplay || step === null) return;

    const timer = setTimeout(() => {
      if (step >= last) finish();
      else goTo(step + 1);
    }, dwell * 1000);

    return () => clearTimeout(timer);
  }, [running, paused, autoplay, step, dwell, last, finish, goTo]);

  // Leaving the page mid-run must end it — whatever it was driving is held open
  // until it does. Only a run that started, so a circuit merely looked at ends
  // nothing.
  const runningRef = useRef(false);
  const stopRef = useRef(stop);
  runningRef.current = running;
  stopRef.current = stop;

  useEffect(
    () => () => {
      if (runningRef.current) stopRef.current();
    },
    []
  );

  return {
    /** The places the run stops at, as indexes into the holds. */
    steps,
    /** Where the run is, as a place in `steps`, and the hold that is. */
    step,
    current,
    running,
    paused,
    playing: running && !paused,
    /** Whether there is a circuit to walk at all: feet alone are no run. */
    walkable: steps.length > 0,
    autoplay,
    setAutoplay,
    dwell,
    setDwell,
    begin,
    stop,
    pause,
    resume,
    stepTo,
    canGoBack: running && step !== null && step > 0,
    canGoOn: running && step !== null && step < last,
  };
}
